import type { TFunction } from "i18next";
import type {
  V2PrivacyReason,
  V2PrivacyState,
} from "../../../redacted/v2/privacy/types";
import { checkedAgo, type PrivacyView } from "./privacyDisplay";

// Plain-language texts for the Reserve privacy indicator. `t` must come from
// `useTranslation("portfolio")`. Labels only: no percentage, no "safe".

/** The visible label of one asset's meter. */
export const levelLabel = (t: TFunction, view: PrivacyView): string =>
  view.pending
    ? t("privacyChecking")
    : view.mixed
      ? t("privacyMixed")
      : view.level === "limited"
        ? t("privacyLimited")
        : view.level === "no_known_link"
          ? t("privacyNoKnownLink")
          : t("privacyUnknown");

/**
 * What a screen reader hears for one meter: the link warning first, then the
 * level with its flags, then that it is only an estimate.
 */
export const meterLabel = (t: TFunction, view: PrivacyView): string => {
  const parts = [t("privacyMeterLabel", { label: levelLabel(t, view) })];
  if (view.mixed)
    parts.push(t("privacyMixedWeakest", { label: t("privacyLimited") }));
  if (view.partlyPublic) parts.push(t("privacyPartlyPublic"));
  const link = view.linkDetected ? `${t("privacyLinkDetected")}. ` : "";
  return `${link}${parts.join(", ")}. ${t("privacyEstimate")}`;
};

export const reasonText = (t: TFunction, reason: V2PrivacyReason): string => {
  switch (reason) {
    case "only_holder":
      return t("privacyReasonOnlyHolder");
    case "largest_holder":
      return t("privacyReasonLargestHolder");
    case "small_pool":
      return t("privacyReasonSmallPool");
    case "unique_note_size":
      return t("privacyReasonUniqueNoteSize");
    case "carried_over":
      return t("privacyReasonCarriedOver");
    case "partly_public":
      return t("privacyReasonPartlyPublic");
    case "spending_only":
      return t("privacyReasonSpendingOnly");
    case "data_unavailable":
      return t("privacyReasonDataUnavailable");
    default:
      // A reason added later still reads as a plain sentence.
      return t("privacyReasonLimited");
  }
};

/**
 * The reasons "Learn more" gives for one asset, most important first.
 * Without `generic`, the line that only restates "No known link" is left out
 * (the panel explains the label next to the list).
 */
export const reasonLines = (
  t: TFunction,
  view: PrivacyView,
  { generic = true }: { generic?: boolean } = {}
): string[] => {
  const lines: string[] = [];
  if (view.linkDetected) lines.push(t("privacyReasonLinkDetected"));
  if (view.pending) return [...lines, t("privacyReasonChecking")];
  if (view.mixed) lines.push(t("privacyReasonMixed"));
  const reasons = view.reasons.filter((reason) => reason !== "partly_public");
  lines.push(...reasons.map((reason) => reasonText(t, reason)));
  if (view.level === "limited" && !reasons.length)
    lines.push(t("privacyReasonLimited"));
  if (view.level === "unknown" && !reasons.length)
    lines.push(t("privacyReasonNoData"));
  if (view.level === "no_known_link" && generic)
    lines.push(t("privacyReasonNoneFound"));
  // "Nothing of it is in the Reserve yet" already says where it is.
  if (
    (view.partlyPublic || view.reasons.includes("partly_public")) &&
    !reasons.includes("spending_only")
  )
    lines.push(t("privacyReasonPartlyPublic"));
  return [...new Set(lines)];
};

/** "Checked 2 minutes ago", "Checking…" or "Not checked yet". */
export const checkedText = (
  t: TFunction,
  state: Pick<V2PrivacyState, "checking" | "updatedAt">,
  now: number,
  language: string
): string => {
  if (state.checking) return t("privacyChecking");
  if (state.updatedAt === undefined) return t("privacyNotChecked");
  const time = checkedAgo(now - state.updatedAt, language);
  return time ? t("privacyChecked", { time }) : t("privacyCheckedJustNow");
};
